import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Receipt, CheckCircle2, Sparkles, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const statusColors = {
  richiesta: "bg-slate-100 text-slate-700", approvata: "bg-blue-100 text-blue-700",
  emessa: "bg-amber-100 text-amber-700", consegnata: "bg-purple-100 text-purple-700",
  pagata: "bg-emerald-100 text-emerald-700", insoluta: "bg-red-100 text-red-700",
  scartata: "bg-rose-100 text-rose-700",
};

function NewInvoiceDialog({ practices, fixedProjectId = null, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: fixedProjectId || "", amount: "", iva_rate: 22, description: "", milestone: "", notes: "" });
  const [aiLoading, setAiLoading] = useState(false);

  const submit = async () => {
    if (!form.project_id || !form.amount || !form.description.trim()) { toast.error("Pratica, importo e descrizione richiesti"); return; }
    try {
      await api.post("/invoices", { ...form, amount: parseFloat(form.amount), iva_rate: parseFloat(form.iva_rate) });
      toast.success("Richiesta fattura creata");
      setOpen(false); setForm({ project_id: fixedProjectId || "", amount: "", iva_rate: 22, description: "", milestone: "", notes: "" });
      onCreated();
    } catch { toast.error("Errore creazione"); }
  };

  const aiDescribe = async () => {
    if (!form.project_id) { toast.error("Seleziona prima una pratica"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/invoices/ai-describe", { project_id: form.project_id });
      setForm(f => ({ ...f, description: data.description }));
      toast.success("Descrizione generata da Claude");
    } catch { toast.error("Errore AI"); }
    finally { setAiLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-1" />Richiedi fattura</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Nuova richiesta di fattura</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!fixedProjectId && (
            <div className="space-y-1.5">
              <Label>Pratica *</Label>
              <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id: v})}>
                <SelectTrigger data-testid="inv-project-select"><SelectValue placeholder="Seleziona pratica"/></SelectTrigger>
                <SelectContent>{practices.map(p => <SelectItem key={p.id} value={p.id}>{p.numero_protocollo} · {p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Importo netto € *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="inv-amount" /></div>
            <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={form.iva_rate} onChange={(e)=>setForm({...form, iva_rate: e.target.value})} /></div>
          </div>
          <div className="space-y-1.5"><Label>Milestone</Label><Input value={form.milestone} onChange={(e)=>setForm({...form, milestone: e.target.value})} placeholder="Es. Acconto 50%"/></div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Descrizione *</Label>
              <Button type="button" variant="ghost" size="sm" disabled={aiLoading || !form.project_id} onClick={aiDescribe} className="text-xs h-7" data-testid="inv-ai-describe">
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1 text-amber-500" />Suggerisci con AI</>}
              </Button>
            </div>
            <Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="inv-description" />
          </div>
          <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={submit} data-testid="inv-save-btn">Crea richiesta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Fatturazione({ projectId = null }) {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [practices, setPractices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("_all");
  const canAdmin = ["managing_partner", "admin", "amministrazione"].includes(user?.role);

  const load = async () => {
    const params = new URLSearchParams();
    if (projectId) params.set("project_id", projectId);
    if (statusFilter !== "_all") params.set("status", statusFilter);
    const { data } = await api.get(`/invoices?${params}`);
    setInvoices(data);
  };
  useEffect(() => { load(); }, [statusFilter, projectId]);
  useEffect(() => { api.get("/practices").then(r => setPractices(r.data)); }, []);

  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}`, { status });
      toast.success(`Fattura → ${status}`);
      load();
    } catch { toast.error("Permesso negato — solo amministrazione"); }
  };
  const remove = async (inv) => {
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Rimossa"); load(); } catch { toast.error("Permesso negato"); }
  };

  const tot = invoices.reduce((a, i) => a + (i.total_amount || 0), 0);
  const totPaid = invoices.filter(i => i.status === "pagata").reduce((a, i) => a + i.total_amount, 0);
  const totPending = tot - totPaid;

  const inner = (
    <div className="space-y-5">
      {!projectId && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-navy-900">Fatturazione</h1>
            <p className="text-sm text-slate-500 mt-1">Richieste, emissioni e incassi</p>
          </div>
          <NewInvoiceDialog practices={practices} onCreated={load} />
        </div>
      )}

      {!projectId && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500">Totale</div>
            <div className="text-2xl font-heading font-bold text-navy-900 mt-1">€ {tot.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="text-xs text-slate-500">{invoices.length} fatture</div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500">Incassato</div>
            <div className="text-2xl font-heading font-bold text-emerald-700 mt-1">€ {totPaid.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </Card>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500">In attesa</div>
            <div className="text-2xl font-heading font-bold text-amber-700 mt-1">€ {totPending.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </Card>
        </div>
      )}

      {!projectId && (
        <Card className="p-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="max-w-xs" data-testid="inv-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tutti gli stati</SelectItem>
              {Object.keys(statusColors).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>
      )}

      {projectId && (
        <div className="flex justify-end">
          <NewInvoiceDialog practices={practices} fixedProjectId={projectId} onCreated={load} />
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2.5">N° / Data</th>
              {!projectId && <th className="text-left px-5 py-2.5">Pratica / Cliente</th>}
              <th className="text-left px-5 py-2.5">Descrizione</th>
              <th className="text-right px-5 py-2.5">Importo</th>
              <th className="text-left px-5 py-2.5">Stato</th>
              <th className="text-right px-5 py-2.5">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} className="border-t border-slate-100" data-testid={`inv-row-${inv.id}`}>
                <td className="px-5 py-3">
                  <div className="font-mono text-xs text-slate-700">{inv.invoice_number || "—"}</div>
                  <div className="text-[11px] text-slate-400">{inv.issue_date || new Date(inv.created_at).toLocaleDateString("it-IT")}</div>
                </td>
                {!projectId && (
                  <td className="px-5 py-3">
                    <div className="text-navy-900 text-sm font-medium">{inv.project_protocol}</div>
                    <div className="text-xs text-slate-500">{inv.client_name || "—"}</div>
                  </td>
                )}
                <td className="px-5 py-3 text-slate-700 max-w-md">
                  <div className="text-sm truncate">{inv.description}</div>
                  {inv.milestone && <div className="text-[11px] text-slate-400">{inv.milestone}</div>}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="font-mono text-navy-900 font-semibold">€ {inv.total_amount.toLocaleString("it-IT", {minimumFractionDigits:2})}</div>
                  <div className="text-[11px] text-slate-400">netto €{inv.amount.toLocaleString("it-IT", {minimumFractionDigits:2})}</div>
                </td>
                <td className="px-5 py-3"><Badge className={`${statusColors[inv.status] || "bg-slate-100"} border-0`}>{inv.status}</Badge></td>
                <td className="px-5 py-3 text-right">
                  {canAdmin && inv.status === "richiesta" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={()=>setStatus(inv, "emessa")} data-testid={`inv-emit-${inv.id}`}>Emetti</Button>
                  )}
                  {canAdmin && inv.status === "emessa" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-200 text-emerald-700 mr-1" onClick={()=>setStatus(inv, "pagata")} data-testid={`inv-paid-${inv.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Incassata</Button>
                  )}
                  {canAdmin && <button onClick={()=>remove(inv)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={projectId ? 5 : 6} className="text-center text-slate-400 py-12">Nessuna fattura.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );

  return inner;
}
